import { memo } from 'react';
import { cn } from '@kidshome/ui';
import type { CollectiblePlugin, ContentKind, ScenePlacement } from './content-plugin';

/**
 * The shared FLAT-COLLECTIBLE renderer (ADR-0114) — pets and backgrounds are the same flat collectible (a
 * painted image resolved by slug, no looks/forms/scenes), so they share ONE parameterized plugin factory
 * instead of the near-identical `PetSprite` / `BackgroundThumb` / `backgroundSceneFor` they used to duplicate.
 * The only differences are the tile FIT (`sprite` = `meet`, the pet floats in its box; `thumb` = `slice`, the
 * backdrop fills it) and whether the kind has a full-bleed staged BACKDROP (`withStage`, backgrounds only).
 * Each per-kind `*.plugin` module supplies its own `import.meta.glob` resolver (Vite needs the literal at the
 * call site) and calls this once.
 */

/** The catalog-tile fit: a pet floats (`meet`), a backdrop fills its 1:1 tile (`slice`). */
type TileFit = 'sprite' | 'thumb';

/** The staged backdrop box HeroStage slots a background into (its `hero-scene-fill` SVG). The backdrop art is
 *  SQUARE (640×640), so the stage is square too — a 1:1 viewBox fills with the WHOLE scene, no crop, and the
 *  per-scene anchors (0–100) address this same square space. */
const STAGE = 100;

/** Build a flat (pet/background) collectible plugin from its slug→url resolver + tile fit + optional backdrop. */
export function flatCollectible(opts: {
  kind: ContentKind;
  assetFor: (slug: string) => string | undefined;
  /** `sprite` (meet) for a pet that floats in its tile; `thumb` (slice) for a backdrop that fills it. */
  tileFit: TileFit;
  /** Backgrounds only: also expose `renderStage` (the full-bleed square backdrop + legibility scrim). */
  withStage?: boolean;
  /** Backgrounds only: per-slug hero + pet anchors on the square scene (where each stands). */
  placementFor?: (slug: string) => ScenePlacement | undefined;
}): CollectiblePlugin {
  const { kind, assetFor, tileFit, withStage, placementFor } = opts;
  const fit = tileFit === 'sprite' ? 'xMidYMid meet' : 'xMidYMid slice';

  // The square shop/collection/wardrobe tile — decorative (`aria-hidden`; the card supplies the name).
  // Memoized (like the old PetSprite/BackgroundThumb) so re-renders don't redraw the <image>.
  const Tile = memo(function Tile({
    id,
    className,
  }: {
    id: string | null;
    className?: string;
  }): React.ReactElement | null {
    const url = id ? assetFor(id) : undefined;
    if (!url) return null;
    return (
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-hidden="true"
        focusable="false"
        preserveAspectRatio={fit}
        className={cn('block', className)}
      >
        <image href={url} x="0" y="0" width="100" height="100" preserveAspectRatio={fit} />
      </svg>
    );
  });

  return {
    kind,
    assetFor,
    renderTile: ({ id, className }) => <Tile id={id} className={className} />,
    // The full-bleed staged BACKDROP (ADR-0110 §7) — the painted scene (`slice`, covers the box) under a soft
    // top-and-bottom legibility SCRIM so the hero reads against ANY equipped background (one legibility floor,
    // never an aesthetic gate). `uid` namespaces the scrim gradient so several stages coexist on one page.
    // Returns null (the hero's own themed sky shows) for an unknown/empty slug.
    renderStage: withStage
      ? ({ id, uid }) => {
          const url = id ? assetFor(id) : undefined;
          if (!url) return null;
          const scrimId = `bg-scrim-${uid}`;
          return (
            <>
              <image
                href={url}
                x="0"
                y="0"
                width={STAGE}
                height={STAGE}
                preserveAspectRatio="xMidYMid slice"
              />
              <defs>
                <linearGradient id={scrimId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#0b1026" stopOpacity="0.22" />
                  <stop offset="0.5" stopColor="#0b1026" stopOpacity="0" />
                  <stop offset="1" stopColor="#0b1026" stopOpacity="0.34" />
                </linearGradient>
              </defs>
              <rect x="0" y="0" width={STAGE} height={STAGE} fill={`url(#${scrimId})`} />
            </>
          );
        }
      : undefined,
    placement: placementFor ? (id) => (id ? (placementFor(id) ?? null) : null) : undefined,
  };
}
